import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, MapPin, Calendar, Clock, Users, Car, Sparkles, MessageCircle, Briefcase, Copy, Check, ArrowLeftRight, Timer, Plane, Wine, Cookie, Wifi, Droplet, Plus, Minus, Ship, Bike, Compass, Info
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { buildWhatsAppLink, buildBookingMessage } from "@/lib/whatsapp";
import PlacesAutocompleteInput from "@/components/PlacesAutocompleteInput";

const UPSELLS = [
  { code: "beverage_water",      i18n: "form.upsell_beverage_water",      price: 3.00,  icon: Droplet, per: "bottle" },
  { code: "beverage_ice_coffee", i18n: "form.upsell_beverage_ice_coffee", price: 5.00,  icon: Wine,    per: "each" },
  { code: "beverage_cola",       i18n: "form.upsell_beverage_cola",       price: 5.00,  icon: Wine,    per: "each" },
  { code: "beverage_beer",       i18n: "form.upsell_beverage_beer",       price: 5.00,  icon: Wine,    per: "each" },
  { code: "beverage_energy",     i18n: "form.upsell_beverage_energy",     price: 5.00,  icon: Wine,    per: "each" },
  { code: "snack_box",           i18n: "form.upsell_snack_box",           price: 13.00, icon: Cookie,  per: "box" },
  { code: "sim_card",            i18n: "form.upsell_sim_card",            price: 20.00, icon: Wifi,    per: "card" },
];

const UPSELL_MAX_QTY = 20;

const CLASS_META = {
  standard: { labelI18n: "form.vehicle_class_comfort",  subI18n: "form.vehicle_class_comfort_sub" },
  xl:       { labelI18n: "form.vehicle_class_xl",       subI18n: "form.vehicle_class_xl_sub" },
  van:      { labelI18n: "form.vehicle_class_van",      subI18n: "form.vehicle_class_van_sub" },
  business: { labelI18n: "form.vehicle_class_business", subI18n: "form.vehicle_class_business_sub" },
  vip_van:  { labelI18n: "form.vehicle_class_vip_van",  subI18n: "form.vehicle_class_vip_van_sub" },
};

const CLASS_PHOTOS = {
  business: { src: "/fleet/mercedes-e-class.jpg", alt: "Mercedes-Benz E-Class" },
  vip_van:  { src: "/fleet/mercedes-v-class.jpg", alt: "Mercedes-Benz V-Class" },
};

const DEFAULT_VEHICLE_CLASSES = [
  { key: "standard", multiplier: 1.00, capacity: 4, ...CLASS_META.standard },
  { key: "xl",       multiplier: 1.15, capacity: 6, ...CLASS_META.xl },
  { key: "van",      multiplier: 1.30, capacity: 8, ...CLASS_META.van },
  { key: "business", multiplier: 1.80, capacity: 3, ...CLASS_META.business },
  { key: "vip_van",  multiplier: 2.20, capacity: 7, ...CLASS_META.vip_van },
];

const FIELD =
  "bg-[#0d0d0d] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 h-12";

export default function BookingForm() {
  const { t } = useLang();
  const today = new Date().toISOString().split("T")[0];

  const [submitting, setSubmitting] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [vehicleClasses, setVehicleClasses] = useState(DEFAULT_VEHICLE_CLASSES);
  const [promoState, setPromoState] = useState({ status: "idle", message: "" }); // idle|ok|err
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    pickup_location: "", dropoff_location: "",
    pickup_coords: null, dropoff_coords: null,
    pickup_date: "", pickup_time: "",
    passengers: 2, luggage: 0, vehicle_type: "Comfort Class", notes: "",
    booking_type: "transfer",   // "transfer" | "hourly"
    duration_hours: 2,          // for hourly, min 2
    is_round_trip: false,
    return_date: "", return_time: "",
    waiting_hours: 0,
    flight_number: "",
    upsells: {},   // { [code]: qty }
    promo_code: "",
    premium_route: false,
    interested_services: [],   // ["boat_charter" | "scooter_quad" | "rent_a_car" | "guided_tours"]
  });

  const isHourly = form.booking_type === "hourly";
  const pickupIsAirport = /airport|aerodrom|zra[cčč]na|\bspu\b/i.test(form.pickup_location || "");

  // Live vehicle classes — synced from the Admin Pricing Dashboard so the
  // multipliers shown here always match the backend (fixes stale +20% Van).
  useEffect(() => {
    api.get("/pricing/catalog").then(({ data }) => {
      const cats = (data?.categories || []).filter((c) => Number(c.multiplier) > 0);
      if (!cats.length) return;
      setVehicleClasses(cats.map((c) => ({
        key: c.key,
        multiplier: Number(c.multiplier) || 1,
        capacity: c.capacity,
        label: c.label,
        ...(CLASS_META[c.key] || {}),
      })));
    }).catch(() => {});
  }, []);

  const selectedClass = vehicleClasses.find((c) => c.key === form.vehicle_category) || vehicleClasses[0];

  // React Router's location — we re-read search params whenever the URL
  // changes so clicking a Popular Route / destination CTA from anywhere on
  // the landing page prefills the form even when BookingForm is already
  // mounted (i.e. we didn't unmount → remount).
  const location = useLocation();

  // Pre-fill pickup/drop-off from URL query (?from=...&to=...&premium=1) — used
  // when a customer clicks "Book Transfer" on a Destinations card. Premium
  // routes also skip the "Get instant estimate" button and auto-fetch.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const q = new URLSearchParams(location.search || window.location.search);
      const from = q.get("from");
      const to = q.get("to");
      const premium = q.get("premium") === "1";
      if (!from && !to) return;
      setForm((f) => ({
        ...f,
        pickup_location: from || f.pickup_location,
        dropoff_location: to || f.dropoff_location,
        pickup_coords: null,
        dropoff_coords: null,
        premium_route: premium,
      }));
      // Focus the booking anchor smoothly, then auto-quote after mount.
      setTimeout(() => {
        const el = document.getElementById("book");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      setTimeout(() => { autoQuote({ from, to, premium }); }, 350);
    } catch { /* no-op */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Fetch quote directly from URL params without waiting for form state
  // to propagate — used by the destination-card auto-quote flow.
  const autoQuote = async ({ from, to, premium }) => {
    if (!from || !to) return;
    setQuoting(true);
    setQuote(null);
    try {
      const { data } = await api.post("/quote", {
        pickup_location: from,
        dropoff_location: to,
        vehicle_type: "Comfort Class",
        pickup_time: "12:00",
        luggage: 0,
        booking_type: "transfer",
        premium_route: !!premium,
      });
      setQuote(data);
    } catch {
      // silent — user can still click "Get instant estimate" manually
    } finally {
      setQuoting(false);
    }
  };

  const upsellsPayload = () =>
    Object.entries(form.upsells || {})
      .map(([code, qty]) => ({ code, qty: Number(qty) || 0 }))
      .filter((u) => u.qty > 0);

  // Persist guest contact to sessionStorage so /experiences can autofill.
  useEffect(() => {
    if (!form.name && !form.email && !form.phone) return;
    try {
      sessionStorage.setItem(
        "juke_guest_contact",
        JSON.stringify({ name: form.name, email: form.email, phone: form.phone }),
      );
    } catch { /* storage disabled */ }
  }, [form.name, form.email, form.phone]);

  // Auto-refresh quote for the sticky price bar.
  // Once pickup + drop-off are both filled (or hourly + duration), silently
  // re-quote 500 ms after any pricing-relevant change so the bar always
  // reflects the current selection without requiring a button click.
  const upsellsKey = JSON.stringify(form.upsells || {});
  useEffect(() => {
    const canQuote =
      (isHourly && form.pickup_location && Number(form.duration_hours) >= 2) ||
      (!isHourly && form.pickup_location && form.dropoff_location);
    if (!canQuote) return;
    const handle = setTimeout(() => { fetchQuote({ silent: true }); }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.pickup_location, form.dropoff_location, form.vehicle_category,
    form.booking_type, form.duration_hours, form.is_round_trip,
    form.waiting_hours, form.luggage, form.pickup_time,
    upsellsKey, form.promo_code, form.premium_route,
  ]);

  const upsellsCount = () =>
    Object.values(form.upsells || {}).reduce((a, b) => a + (Number(b) || 0), 0);

  const setUpsellQty = (code, qty) => {
    setQuote(null);
    const clamped = Math.max(0, Math.min(UPSELL_MAX_QTY, Number(qty) || 0));
    setForm((f) => {
      const next = { ...(f.upsells || {}) };
      if (clamped === 0) delete next[code]; else next[code] = clamped;
      return { ...f, upsells: next };
    });
  };

  const incUpsell = (code) => setUpsellQty(code, (form.upsells?.[code] || 0) + 1);
  const decUpsell = (code) => setUpsellQty(code, (form.upsells?.[code] || 0) - 1);

  const setBookingType = (type) => {
    setQuote(null);
    setForm((f) => ({
      ...f,
      booking_type: type,
      vehicle_type: "Comfort Class",
      dropoff_location: type === "hourly" ? "" : f.dropoff_location,
      is_round_trip: type === "hourly" ? false : f.is_round_trip,
    }));
  };

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // If user manually edits address, invalidate the previously-selected coords.
    if (k === "pickup_location") setForm((f) => ({ ...f, pickup_coords: null }));
    if (k === "dropoff_location") setForm((f) => ({ ...f, dropoff_coords: null }));
    // Route-level changes clear the stale quote. Modifier-level changes
    // (class / luggage / promo / waiting) keep the last quote visible while
    // the debounced auto-refresh recomputes — smoother sticky-bar UX.
    if (["pickup_location", "dropoff_location", "booking_type",
         "duration_hours", "is_round_trip"].includes(k)) {
      setQuote(null);
    }
    if (k === "promo_code") setPromoState({ status: "idle", message: "" });
  };

  const applyPromo = async () => {
    const code = (form.promo_code || "").trim().toUpperCase();
    if (!code) return;
    try {
      const { data } = await api.get(`/promo/${encodeURIComponent(code)}`);
      setPromoState({
        status: "ok",
        message: `${data.code}: ${data.discount_percent}% off applied. Fetch a new quote to see the discount.`,
      });
      setForm((f) => ({ ...f, promo_code: data.code }));
      setQuote(null);
    } catch (err) {
      setPromoState({
        status: "err",
        message: err?.response?.data?.detail || "Invalid or expired promo code",
      });
    }
  };

  const fetchQuote = async ({ silent = false } = {}) => {
    if (!form.pickup_location) {
      if (!silent) toast.error(t("form.err_missing"));
      return;
    }
    if (!isHourly && !form.dropoff_location) {
      if (!silent) toast.error(t("form.err_missing"));
      return;
    }
    if (isHourly && (!form.duration_hours || form.duration_hours < 2)) {
      if (!silent) toast.error(t("form.err_missing"));
      return;
    }
    setQuoting(true);
    if (!silent) setQuote(null);
    try {
      const { data } = await api.post("/quote", {
        pickup_location: form.pickup_location,
        dropoff_location: isHourly ? "" : form.dropoff_location,
        pickup_coords: form.pickup_coords || null,
        dropoff_coords: isHourly ? null : (form.dropoff_coords || null),
        vehicle_type: "Comfort Class",
        vehicle_category: form.vehicle_category || "standard",
        pickup_time: form.pickup_time || "",
        luggage: Number(form.luggage) || 0,
        booking_type: form.booking_type,
        duration_hours: Number(form.duration_hours) || 0,
        is_round_trip: !!form.is_round_trip,
        waiting_hours: Number(form.waiting_hours) || 0,
        upsells: upsellsPayload(),
        promo_code: (form.promo_code || "").trim().toUpperCase(),
        premium_route: !!form.premium_route,
      });
      setQuote(data);
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || t("form.quote_err"));
    } finally {
      setQuoting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const requiredMissing =
      !form.name || !form.email || !form.phone ||
      !form.pickup_location || !form.pickup_date || !form.pickup_time ||
      (!isHourly && !form.dropoff_location) ||
      (isHourly && (!form.duration_hours || form.duration_hours < 2)) ||
      (!isHourly && form.is_round_trip && (!form.return_date || !form.return_time));
    if (requiredMissing) {
      toast.error(t("form.err_missing"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...form,
        vehicle_type: "Comfort Class",
        vehicle_category: form.vehicle_category || "standard",
        passengers: Number(form.passengers) || 1,
        luggage: Number(form.luggage) || 0,
        duration_hours: Number(form.duration_hours) || 0,
        waiting_hours: Number(form.waiting_hours) || 0,
        upsells: upsellsPayload(),
        interested_services: Array.isArray(form.interested_services) ? form.interested_services : [],
        flight_number: (form.flight_number || "").trim().toUpperCase(),
        promo_code: (form.promo_code || "").trim().toUpperCase(),
        premium_route: !!form.premium_route,
      });
      toast.success(t("form.ok"));
      setForm({
        name: "", email: "", phone: "",
        pickup_location: "", dropoff_location: "",
        pickup_coords: null, dropoff_coords: null,
        pickup_date: "", pickup_time: "",
        passengers: 2, luggage: 0, vehicle_type: "Comfort Class", notes: "",
        vehicle_category: "standard",
        booking_type: "transfer", duration_hours: 2, is_round_trip: false,
        return_date: "", return_time: "", waiting_hours: 0,
        flight_number: "", upsells: {}, promo_code: "", premium_route: false,
        interested_services: [],
      });
      setPromoState({ status: "idle", message: "" });
      setQuote(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || t("form.err_submit");
      toast.error(typeof msg === "string" ? msg : t("form.err_submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const [copied, setCopied] = useState(false);
  const copyBreakdown = async () => {
    if (!quote) return;
    const dur = `${Math.floor(quote.duration_min/60)}h ${quote.duration_min%60}m`;
    const lines = [
      "Juke Transfers — Estimate",
      `${quote.pickup_resolved.split(",")[0]} → ${quote.dropoff_resolved.split(",")[0]}`,
      `${quote.distance_km} km · ${dur}${form.pickup_time ? " · " + form.pickup_time : ""}${form.luggage ? " · " + form.luggage + " suitcases" : ""}`,
      "",
      quote.night_surcharge ? "Nighttime rate (22:00–06:00)" : "Daytime rate (06:00–22:00)",
      quote.is_fixed_route ? "Fixed route: yes" : "",
      "",
    ];
    if (quote.is_fixed_route) {
      lines.push(`Published fixed price       €${quote.base_price_eur.toFixed(2)}`);
    } else {
      lines.push(`Start fee                    €${quote.rate_start_fee.toFixed(2)}`);
      lines.push(`${quote.distance_km} km × €${quote.rate_per_km.toFixed(2)}     €${(quote.distance_km * quote.rate_per_km).toFixed(2)}`);
      if (quote.min_fare_applied) lines.push(`Minimum fare applied         €${quote.base_price_eur.toFixed(2)}`);
    }
    if (quote.category_multiplier > 1) {
      lines.push(`Vehicle class ×${Number(quote.category_multiplier).toFixed(2)}         +€${quote.category_uplift_eur.toFixed(2)}`);
    }
    if (quote.luggage_fee_eur > 0) {
      const n = Math.round(quote.luggage_fee_eur / quote.rate_per_suitcase);
      lines.push(`${n} × €${quote.rate_per_suitcase.toFixed(2)} suitcases       €${quote.luggage_fee_eur.toFixed(2)}`);
    }
    lines.push("─────────────────────────");
    if (quote.discount_eur > 0) {
      lines.push(`Subtotal                     €${(quote.subtotal_eur || (quote.total_price_eur + quote.discount_eur)).toFixed(2)}`);
      lines.push(`${quote.promo_code} (−${quote.discount_percent}%)     −€${quote.discount_eur.toFixed(2)}`);
    }
    lines.push(`Total                        €${quote.total_price_eur.toFixed(2)}`);
    lines.push("");
    lines.push("bookings@juketransfers.hr · juketransfers.hr");
    const text = lines.filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(t("form.rate_copy_ok"));
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(t("form.rate_copy_err"));
    }
  };

  return (
    <>
    <form
      data-testid="booking-form"
      onSubmit={submit}
      className="relative rounded-2xl bg-black/60 backdrop-blur-2xl border border-white/10 shadow-[0_8px_48px_rgba(0,0,0,0.6)] p-6 sm:p-8 lg:p-10"
    >
      <div className="absolute -top-3 left-8 px-3 py-1 bg-[#C5A059] text-black text-[10px] font-semibold uppercase tracking-[0.2em] rounded">
        {t("form.badge")}
      </div>

      <h3 className="font-display text-2xl sm:text-3xl text-white font-light mb-1">{t("form.title")}</h3>
      <p className="text-sm text-zinc-400 mb-6">{t("form.sub")}</p>

      {/* Vehicle class selector — always visible on the main widget.
          Shows all classes with their price multiplier; pre-selected is highlighted.
          Only affects the booking flow (not the public /fleet page). */}
      <div className="mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{t("form.vehicle_class_eyebrow")}</p>
          <p className="text-[10px] text-zinc-500">{t("form.vehicle_class_helper")}</p>
        </div>
        <div data-testid="vehicle-class-selector" className="grid grid-cols-3 gap-2">
          {vehicleClasses.map((cls) => {
            const active = form.vehicle_category === cls.key;
            const pctLabel = `×${Number(cls.multiplier).toFixed(2)}`;
            const photo = CLASS_PHOTOS[cls.key];
            return (
              <button
                key={cls.key}
                type="button"
                data-testid={`vehicle-class-${cls.key}`}
                onClick={() => update("vehicle_category", cls.key)}
                className={`relative flex flex-col items-center text-center gap-1 px-2 rounded-xl border transition-all min-w-0 overflow-hidden ${
                  photo ? "pt-0 pb-3" : "py-3"
                } ${
                  active
                    ? "bg-[#C5A059]/10 border-[#C5A059] shadow-[0_0_0_1px_rgba(197,160,89,0.35)]"
                    : "bg-black/40 border-white/10 hover:border-[#C5A059]/40"
                }`}
              >
                {photo ? (
                  <span className="relative block w-[calc(100%+1rem)] -mx-2 mb-1">
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      loading="lazy"
                      data-testid={`vehicle-class-photo-${cls.key}`}
                      className={`w-full h-14 sm:h-16 object-cover transition-opacity ${active ? "opacity-100" : "opacity-85"}`}
                    />
                    <span className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                  </span>
                ) : (
                  <Car className={`w-4 h-4 shrink-0 ${active ? "text-[#C5A059]" : "text-zinc-500"}`} />
                )}
                <span className={`text-xs sm:text-sm font-medium leading-tight break-words ${
                  active ? "text-white" : "text-zinc-200"
                }`}>
                  {cls.labelI18n ? t(cls.labelI18n) : cls.label}
                </span>
                <span className={`text-[10px] leading-tight break-words ${
                  active ? "text-[#C5A059]" : "text-zinc-500"
                }`}>
                  {cls.subI18n ? t(cls.subI18n) : ""}
                </span>
                <span className={`mt-0.5 text-[9px] uppercase tracking-wider font-mono ${
                  active ? "text-[#C5A059]" : "text-zinc-600"
                }`}>
                  {pctLabel}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Booking type tabs */}
      <div data-testid="booking-type-tabs" className="mb-6 grid grid-cols-2 gap-1 p-1 rounded-full bg-black/50 border border-white/10 text-xs">
        {[
          { v: "transfer", label: t("form.tab_transfer") },
          { v: "hourly",   label: t("form.tab_hourly") },
        ].map((tab) => (
          <button
            key={tab.v}
            type="button"
            data-testid={`booking-tab-${tab.v}`}
            onClick={() => setBookingType(tab.v)}
            className={`h-9 rounded-full uppercase tracking-[0.2em] font-medium transition-colors ${
              form.booking_type === tab.v
                ? "bg-[#C5A059] text-black"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label={t("form.name")} htmlFor="name">
          <Input id="name" data-testid="booking-name-input" value={form.name}
                 onChange={(e) => update("name", e.target.value)} placeholder={t("form.name_ph")}
                 className={FIELD} required />
        </Field>

        <Field label={t("form.email")} htmlFor="email">
          <Input id="email" type="email" data-testid="booking-email-input" value={form.email}
                 onChange={(e) => update("email", e.target.value)} placeholder={t("form.email_ph")}
                 className={FIELD} required />
        </Field>

        <Field label={t("form.phone")} htmlFor="phone">
          <Input id="phone" data-testid="booking-phone-input" value={form.phone}
                 onChange={(e) => update("phone", e.target.value)} placeholder={t("form.phone_ph")}
                 className={FIELD} required />
        </Field>

        <Field label={t("form.passengers")}>
          <div className="relative">
            <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <Input type="number" min={1} max={20} data-testid="booking-passengers-input"
                   value={form.passengers}
                   onChange={(e) => update("passengers", e.target.value)}
                   className={`${FIELD} pl-10`} required />
          </div>
        </Field>

        <Field label={t("form.luggage")}>
          <div className="relative">
            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <Input type="number" min={0} max={20} data-testid="booking-luggage-input"
                   value={form.luggage}
                   onChange={(e) => update("luggage", e.target.value)}
                   placeholder={t("form.luggage_ph")}
                   className={`${FIELD} pl-10`} />
          </div>
        </Field>

        <Field label={t("form.pickup")} htmlFor="pickup">
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none z-10" />
            <PlacesAutocompleteInput
              id="pickup"
              testId="booking-pickup-input"
              value={form.pickup_location}
              onChange={(v) => update("pickup_location", v)}
              onPlaceSelected={(p) => {
                setForm((f) => ({ ...f, pickup_location: p.name, pickup_coords: { lat: p.lat, lon: p.lon } }));
                setQuote(null);
              }}
              placeholder={t("form.pickup_ph")}
              className={`${FIELD} pl-10`}
              required
            />
          </div>
        </Field>

        {isHourly ? (
          <Field label={
            <span className="inline-flex items-center gap-1.5">
              {t("form.duration")}
              <span tabIndex={0} data-testid="duration-fairuse-info" className="relative group cursor-help outline-none">
                <Info className="w-3.5 h-3.5 text-zinc-500 group-hover:text-[#C5A059] group-focus-within:text-[#C5A059] transition-colors" />
                <span
                  data-testid="duration-fairuse-tooltip"
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 hidden group-hover:block group-focus-within:block z-50 rounded-lg border border-white/10 bg-[#0d0d0d] p-3 text-[11px] leading-relaxed text-zinc-300 normal-case tracking-normal shadow-2xl"
                >
                  {t("form.duration_fairuse")}
                </span>
              </span>
            </span>
          }>
            <div className="relative">
              <Timer className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
              <Select value={String(form.duration_hours)} onValueChange={(v) => update("duration_hours", Number(v))}>
                <SelectTrigger data-testid="booking-duration-select" className={`${FIELD} pl-10`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0d0d0d] border-white/10 text-zinc-100">
                  {[2,3,4,5,6,8,10,12].map((h) => (
                    <SelectItem key={h} value={String(h)} data-testid={`duration-option-${h}`}>
                      {h} {t("form.hours")} · €{Math.round(h * 60 * (selectedClass?.multiplier || 1))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Field>
        ) : (
          <Field label={t("form.dropoff")} htmlFor="dropoff">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none z-10" />
              <PlacesAutocompleteInput
                id="dropoff"
                testId="booking-dropoff-input"
                value={form.dropoff_location}
                onChange={(v) => update("dropoff_location", v)}
                onPlaceSelected={(p) => {
                  setForm((f) => ({ ...f, dropoff_location: p.name, dropoff_coords: { lat: p.lat, lon: p.lon } }));
                  setQuote(null);
                }}
                placeholder={t("form.dropoff_ph")}
                className={`${FIELD} pl-10`}
                required={!isHourly}
              />
            </div>
          </Field>
        )}

        <Field label={t("form.date")} htmlFor="date">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <Input id="date" type="date" min={today} data-testid="booking-date-input"
                   value={form.pickup_date}
                   onChange={(e) => update("pickup_date", e.target.value)}
                   className={`${FIELD} pl-10`} required />
          </div>
        </Field>

        <Field label={t("form.time")} htmlFor="time">
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <Input id="time" type="time" data-testid="booking-time-input"
                   value={form.pickup_time}
                   onChange={(e) => update("pickup_time", e.target.value)}
                   className={`${FIELD} pl-10`} required />
          </div>
        </Field>

        {/* Round Trip toggle + return leg fields */}
        {!isHourly && (
          <div className="md:col-span-2 rounded-xl border border-white/10 bg-black/30 p-4">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="booking-round-trip-toggle"
                checked={form.is_round_trip}
                onChange={(e) => update("is_round_trip", e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-black text-[#C5A059] focus:ring-[#C5A059]/40 focus:ring-1"
              />
              <ArrowLeftRight className="w-4 h-4 text-[#C5A059]" />
              <span className="text-sm text-zinc-100 font-medium">{t("form.round_trip")}</span>
              <span className="text-xs text-zinc-500 ml-auto">{t("form.round_trip_hint")}</span>
            </label>

            {form.is_round_trip && (
              <div data-testid="round-trip-fields" className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <Field label={t("form.return_date")}>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                    <Input type="date" min={form.pickup_date || today}
                           data-testid="booking-return-date-input"
                           value={form.return_date}
                           onChange={(e) => update("return_date", e.target.value)}
                           className={`${FIELD} pl-10`} />
                  </div>
                </Field>
                <Field label={t("form.return_time")}>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                    <Input type="time"
                           data-testid="booking-return-time-input"
                           value={form.return_time}
                           onChange={(e) => update("return_time", e.target.value)}
                           className={`${FIELD} pl-10`} />
                  </div>
                </Field>
                <Field label={t("form.waiting_time")}>
                  <div className="relative">
                    <Timer className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none z-10" />
                    <Select value={String(form.waiting_hours)} onValueChange={(v) => update("waiting_hours", Number(v))}>
                      <SelectTrigger data-testid="booking-waiting-select" className={`${FIELD} pl-10`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0d0d0d] border-white/10 text-zinc-100">
                        {[0, 0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 8].map((h) => (
                          <SelectItem key={h} value={String(h)} data-testid={`waiting-option-${h}`}>
                            {h === 0 ? t("form.waiting_none") : `${h} ${t("form.hours")}${h <= 0.5 ? " · " + t("form.waiting_free") : ""}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </Field>
              </div>
            )}
          </div>
        )}

        {pickupIsAirport && !isHourly && (
          <Field label={t("form.flight_number")} htmlFor="flight_number">
            <div className="relative">
              <Plane className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
              <Input
                id="flight_number"
                data-testid="booking-flight-number-input"
                value={form.flight_number}
                onChange={(e) => update("flight_number", e.target.value.toUpperCase())}
                placeholder={t("form.flight_number_ph")}
                maxLength={8}
                className={`${FIELD} pl-10 uppercase tracking-wider`}
              />
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">{t("form.flight_number_hint")}</p>
          </Field>
        )}

        {/* Upsells / add-ons — quantity based */}
        <div className="md:col-span-2 rounded-xl border border-white/10 bg-black/30 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">{t("form.upsell_eyebrow")}</p>
              <p className="text-sm text-zinc-300 mt-0.5">{t("form.upsell_title")}</p>
            </div>
            {upsellsCount() > 0 && (
              <span data-testid="upsell-count-badge" className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] border border-[#C5A059]/30 rounded-full px-2 py-0.5">
                {upsellsCount()} {t("form.upsell_selected")}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {UPSELLS.map((u) => {
              const qty = Number(form.upsells?.[u.code] || 0);
              const active = qty > 0;
              const Icon = u.icon;
              return (
                <div
                  key={u.code}
                  data-testid={`upsell-${u.code}`}
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border transition-colors ${
                    active
                      ? "bg-[#C5A059]/10 border-[#C5A059]/60"
                      : "bg-black/40 border-white/10"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <Icon className={`w-4 h-4 shrink-0 ${active ? "text-[#C5A059]" : "text-zinc-500"}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm leading-snug break-words ${active ? "text-white" : "text-zinc-300"}`}>{t(u.i18n)}</p>
                      <p className="text-[11px] text-zinc-500">€{u.price.toFixed(2)}{qty > 0 && ` · subtotal €${(u.price * qty).toFixed(2)}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => decUpsell(u.code)}
                      disabled={qty === 0}
                      data-testid={`upsell-dec-${u.code}`}
                      aria-label="Decrease"
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        qty === 0
                          ? "bg-white/5 text-zinc-600 cursor-not-allowed"
                          : "bg-white/10 text-white hover:bg-[#C5A059] hover:text-black"
                      }`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span
                      data-testid={`upsell-qty-${u.code}`}
                      className={`w-8 text-center text-sm tabular-nums font-medium ${active ? "text-[#C5A059]" : "text-zinc-500"}`}
                    >
                      {qty}
                    </span>
                    <button
                      type="button"
                      onClick={() => incUpsell(u.code)}
                      disabled={qty >= UPSELL_MAX_QTY}
                      data-testid={`upsell-inc-${u.code}`}
                      aria-label="Increase"
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        qty >= UPSELL_MAX_QTY
                          ? "bg-white/5 text-zinc-600 cursor-not-allowed"
                          : "bg-[#C5A059] text-black hover:bg-[#D4AF37]"
                      }`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-2 md:col-span-2">
          <Label htmlFor="notes" className="text-xs uppercase tracking-wider text-zinc-400">
            {t("form.notes")} <span className="text-zinc-600 normal-case tracking-normal">{t("form.notes_optional")}</span>
          </Label>
          <Textarea id="notes" data-testid="booking-notes-input" value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder={t("form.notes_placeholder")}
                    className="bg-[#0d0d0d] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 min-h-[88px]" />
        </div>
      </div>

      {/* Quote engine */}
      <div className="mt-6 rounded-xl border border-dashed border-[#C5A059]/30 bg-[#C5A059]/[0.04] p-5">
        {/* Promo code row */}
        <div className="mb-4">
          <Label htmlFor="promo" className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            Promo code
          </Label>
          <div className="mt-1 flex gap-2">
            <Input
              id="promo"
              data-testid="booking-promo-input"
              value={form.promo_code}
              onChange={(e) => update("promo_code", e.target.value.toUpperCase())}
              placeholder="e.g. ENTERCODE"
              className={`${FIELD} uppercase tracking-widest`}
              maxLength={16}
            />
            <button
              type="button"
              data-testid="booking-promo-apply-btn"
              onClick={applyPromo}
              disabled={!form.promo_code}
              className="shrink-0 px-4 h-12 rounded-md border border-[#C5A059]/50 text-[#C5A059] hover:bg-[#C5A059]/10 disabled:opacity-40 text-[11px] uppercase tracking-[0.2em]"
            >
              Apply
            </button>
          </div>
          {promoState.message && (
            <p
              data-testid="booking-promo-msg"
              className={`mt-2 text-[11px] ${promoState.status === "ok" ? "text-emerald-400" : "text-red-400"}`}
            >
              {promoState.message}
            </p>
          )}
        </div>

        {!quote ? (
          <button
            type="button"
            data-testid="quote-button"
            onClick={fetchQuote}
            disabled={quoting}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-full bg-transparent border border-[#C5A059]/50 text-[#C5A059] hover:bg-[#C5A059]/10 transition-colors disabled:opacity-50"
          >
            {quoting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("form.quote_btn_loading")}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                {t("form.quote_btn")}
              </>
            )}
          </button>
        ) : (
          <div data-testid="quote-result" className="space-y-3">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {quote.booking_type === "hourly" ? t("form.duration") : t("form.quote_distance")}
                </p>
                <p className="font-display text-xl text-white mt-1">
                  {quote.booking_type === "hourly" ? `${quote.duration_hours}h` : `${quote.distance_km} km`}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {quote.booking_type === "hourly" ? t("form.rate_hourly_short") : t("form.quote_duration")}
                </p>
                <p className="font-display text-xl text-white mt-1">
                  {quote.booking_type === "hourly"
                    ? `€${quote.hourly_rate_eur}/h`
                    : `${Math.floor(quote.duration_min/60)}h ${quote.duration_min%60}m`}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">{t("form.quote_estimate")}</p>
                <p className="font-display text-2xl text-[#C5A059] mt-1">€{Math.round(quote.total_price_eur)}</p>
              </div>
            </div>

            {/* Rate breakdown */}
            <div data-testid="rate-breakdown" className="rounded-lg border border-white/5 bg-black/40 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {quote.booking_type === "hourly"
                    ? t("form.rate_hourly_title")
                    : quote.night_surcharge ? t("form.rate_night_title") : t("form.rate_day_title")}
                </p>
                <div className="flex items-center gap-2">
                  {quote.is_round_trip && (
                    <span className="text-[9px] uppercase tracking-[0.2em] text-[#C5A059] border border-[#C5A059]/30 rounded-full px-2 py-0.5">
                      {t("form.rate_round_trip_badge")}
                    </span>
                  )}
                  {quote.is_fixed_route && (
                    <span className="text-[9px] uppercase tracking-[0.2em] text-[#C5A059] border border-[#C5A059]/30 rounded-full px-2 py-0.5">
                      {t("form.rate_fixed_badge")}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-[12px] text-zinc-400 space-y-1">
                {quote.has_upsells ? (
                  <div className="flex justify-between">
                    <span className="text-zinc-200">{t("form.rate_premium_transfer_line")}</span>
                    <span className="text-zinc-200 tabular-nums">€{quote.total_price_eur.toFixed(2)}</span>
                  </div>
                ) : quote.booking_type === "hourly" ? (
                  <div className="flex justify-between">
                    <span>{quote.duration_hours} {t("form.hours")} × €{quote.hourly_rate_eur.toFixed(2)}</span>
                    <span className="text-zinc-200 tabular-nums">€{quote.base_price_eur.toFixed(2)}</span>
                  </div>
                ) : quote.is_fixed_route ? (
                  <div className="flex justify-between">
                    <span>{t("form.rate_fixed_line")}</span>
                    <span className="text-zinc-200 tabular-nums">€{quote.base_price_eur.toFixed(2)}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span>{t("form.rate_start_fee")}</span>
                      <span className="text-zinc-200 tabular-nums">€{quote.rate_start_fee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{quote.distance_km} km × €{quote.rate_per_km.toFixed(2)}</span>
                      <span className="text-zinc-200 tabular-nums">€{(quote.distance_km * quote.rate_per_km).toFixed(2)}</span>
                    </div>
                  </>
                )}
                {!quote.has_upsells && quote.is_round_trip && quote.return_leg_eur > 0 && (
                  <div className="flex justify-between">
                    <span>{t("form.rate_return_leg")}</span>
                    <span className="text-zinc-200 tabular-nums">€{quote.return_leg_eur.toFixed(2)}</span>
                  </div>
                )}
                {!quote.has_upsells && quote.luggage_fee_eur > 0 && (
                  <div className="flex justify-between">
                    <span>{Math.round(quote.luggage_fee_eur / quote.rate_per_suitcase)} × €{quote.rate_per_suitcase.toFixed(2)} {t("form.rate_suitcases")}</span>
                    <span className="text-zinc-200 tabular-nums">€{quote.luggage_fee_eur.toFixed(2)}</span>
                  </div>
                )}
                {!quote.has_upsells && quote.is_round_trip && quote.waiting_hours > 0 && (
                  <div className="flex justify-between">
                    <span>
                      {quote.waiting_hours} {t("form.hours")} {t("form.waiting_at_dest")}
                      {quote.waiting_fee_eur === 0 && <span className="text-[#C5A059]"> · {t("form.waiting_included")}</span>}
                    </span>
                    <span className="text-zinc-200 tabular-nums">€{quote.waiting_fee_eur.toFixed(2)}</span>
                  </div>
                )}
                {!quote.has_upsells && quote.min_fare_applied && (
                  <div className="flex justify-between" data-testid="rate-min-fare-line">
                    <span>{t("form.rate_min_fare")}</span>
                    <span className="text-zinc-200 tabular-nums">€{quote.base_price_eur.toFixed(2)}</span>
                  </div>
                )}
                {!quote.has_upsells && quote.category_multiplier > 1 && (
                  <div className="flex justify-between" data-testid="rate-class-line">
                    <span>{t("form.rate_vehicle_class")} ×{Number(quote.category_multiplier).toFixed(2)}</span>
                    <span className="text-zinc-200 tabular-nums">+€{quote.category_uplift_eur.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 mt-1 border-t border-white/5">
                  <span className="text-zinc-300">{t("form.rate_total")}</span>
                  <span className="text-[#C5A059] font-semibold tabular-nums">€{quote.total_price_eur.toFixed(2)}</span>
                </div>
                {quote.discount_eur > 0 && (
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-emerald-400 text-[11px] uppercase tracking-[0.15em]">
                      {quote.promo_code} · −{quote.discount_percent}% applied
                    </span>
                    <span className="text-emerald-400 tabular-nums">−€{quote.discount_eur.toFixed(2)}</span>
                  </div>
                )}
              </div>
              {quote.night_surcharge && quote.is_fixed_route && (
                <p className="text-[10px] text-zinc-500 pt-1">{t("form.rate_fixed_night_note")}</p>
              )}

              <button
                type="button"
                onClick={copyBreakdown}
                data-testid="copy-breakdown-btn"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#C5A059]/40 text-[11px] uppercase tracking-[0.2em] text-zinc-300 hover:text-[#C5A059] transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? t("form.rate_copy_done") : t("form.rate_copy_btn")}
              </button>
            </div>

            <p className="text-[11px] text-zinc-500 text-center leading-relaxed">{t("form.quote_disclaimer")}</p>
            <a
              href={buildWhatsAppLink(buildBookingMessage({ t, form, quote }))}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quote-whatsapp-button"
              className="mt-1 w-full flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm font-semibold transition-colors shadow-[0_4px_18px_rgba(37,211,102,0.35)]"
            >
              <MessageCircle className="w-4 h-4" />
              {t("form.whatsapp_book")}
            </a>
          </div>
        )}
      </div>

      <CrossSellSection
        selected={form.interested_services}
        onChange={(services) => update("interested_services", services)}
      />

      <button
        type="submit"
        data-testid="booking-submit-button"
        disabled={submitting}
        className="mt-6 w-full group flex items-center justify-center gap-3 h-14 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold tracking-wide transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_36px_rgba(197,160,89,0.4)]"
      >
        {submitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            {t("form.submit")}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </>
        )}
      </button>

      <p className="mt-4 text-center text-xs text-zinc-500">{t("form.footer")}</p>

      <p
        data-testid="booking-model-note"
        className="mt-3 text-[10px] leading-relaxed text-zinc-500 text-center border-t border-white/5 pt-3"
      >
        {t("form.booking_model_note")}
      </p>
    </form>

    {/* Sticky Price Bar — appears once route + class are set. Fixed to the
        viewport bottom so the user always sees the current total no matter
        how far down the form they scroll. */}
    <StickyPriceBar
      quote={quote}
      quoting={quoting}
      form={form}
      isHourly={isHourly}
      vehicleClass={selectedClass}
      submitting={submitting}
      onBook={submit}
      t={t}
    />
    </>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
      {children}
    </div>
  );
}


const CROSS_SELL_OPTIONS = [
  {
    code: "boat_charter",
    label: "Rent a Boat / Charter",
    subLabel: "Speedboats · Jet Skis · Yachts",
    Icon: Ship,
  },
  {
    code: "scooter_quad",
    label: "Rent a Scooter or Quad",
    subLabel: "Vespa · 500cc ATV",
    Icon: Bike,
  },
  {
    code: "rent_a_car",
    label: "Rent a Car",
    subLabel: "Sedan · SUV · Hybrid",
    Icon: Car,
  },
  {
    code: "guided_tours",
    label: "Guided Tours & Experiences",
    subLabel: "Wine · Blue Cave · Adventure",
    Icon: Compass,
  },
];

function CrossSellSection({ selected = [], onChange }) {
  const toggle = (code) => {
    const set = new Set(selected);
    if (set.has(code)) set.delete(code); else set.add(code);
    onChange(Array.from(set));
  };
  return (
    <div
      data-testid="cross-sell-section"
      className="mt-8 rounded-2xl border border-[#C5A059]/25 bg-[#0a0a0a] p-5 lg:p-6"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="mt-1 h-8 w-8 rounded-full border border-[#C5A059]/40 bg-[#C5A059]/5 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-[#C5A059]" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Optional · No upfront charge</p>
          <h3 className="mt-1 font-display text-xl lg:text-2xl text-white font-light tracking-tight">
            Enhance Your Trip
          </h3>
          <p className="mt-1 text-xs text-zinc-400 max-w-lg leading-relaxed">
            Tick anything you&apos;d like our concierge team to arrange around your transfer.
            We&apos;ll come back to you with availability &amp; the best rates.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {CROSS_SELL_OPTIONS.map((opt) => {
          const isOn = selected.includes(opt.code);
          return (
            <button
              type="button"
              key={opt.code}
              data-testid={`cross-sell-${opt.code}`}
              aria-pressed={isOn}
              onClick={() => toggle(opt.code)}
              className={`flex items-center gap-3 text-left rounded-xl border px-4 py-3 transition-all ${
                isOn
                  ? "border-[#C5A059] bg-[#C5A059]/10 shadow-[0_0_0_1px_rgba(197,160,89,0.35)]"
                  : "border-white/10 bg-zinc-950 hover:border-[#C5A059]/40"
              }`}
            >
              <div className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${
                isOn ? "border-[#C5A059] bg-[#C5A059]" : "border-white/20"
              }`}>
                {isOn && <Check className="w-3.5 h-3.5 text-black" />}
              </div>
              <opt.Icon className={`w-4 h-4 shrink-0 ${isOn ? "text-[#C5A059]" : "text-zinc-500"}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm truncate ${isOn ? "text-white" : "text-zinc-200"}`}>{opt.label}</p>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{opt.subLabel}</p>
              </div>
            </button>
          );
        })}
      </div>

      <p className="mt-3 text-[10px] leading-relaxed text-zinc-500">
        No upfront payment required for these add-ons. Simply check the boxes, and our
        concierge team will check availability and send the best rates directly to
        your inbox / WhatsApp.
      </p>
    </div>
  );
}



function StickyPriceBar({ quote, quoting, form, isHourly, vehicleClass, submitting, onBook, t }) {
  // Only render once the user has entered enough to warrant a quote —
  // pickup+dropoff for transfer, pickup+duration for hourly.
  const canQuote =
    (isHourly && form.pickup_location && Number(form.duration_hours) >= 2) ||
    (!isHourly && form.pickup_location && form.dropoff_location);

  if (!canQuote) return null;

  const price = quote?.total_price_eur ?? null;
  const showRecalc = quoting;
  const showBar = price !== null || showRecalc;
  if (!showBar) return null;

  const routeLabel = isHourly
    ? `${t("form.hourly_label") || "Hourly"} · ${form.duration_hours}h`
    : `${form.pickup_location} → ${form.dropoff_location}`;

  // Format distance / duration when available. Prefer the values the API
  // returns (km + min) — works for both regular and fixed-price routes now.
  const km = Number(quote?.distance_km || 0);
  const mins = Number(quote?.duration_min || 0);
  const metricsBits = [];
  if (km > 0) metricsBits.push(`${km.toFixed(km >= 100 ? 0 : 1)} km`);
  if (mins > 0) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    metricsBits.push(h > 0 ? (m ? `~${h}h ${m}m` : `~${h}h`) : `~${m}m`);
  }
  const metricsLine = metricsBits.join(" · ");

  const scrollToSubmit = () => {
    const el = document.querySelector('[data-testid="booking-submit-button"]');
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // If the required fields are all filled, invoke the submit directly.
      setTimeout(() => {
        const allFilled =
          form.name && form.email && form.phone && form.pickup_date && form.pickup_time;
        if (allFilled && typeof onBook === "function") {
          onBook({ preventDefault: () => {} });
        }
      }, 400);
    }
  };

  // Portal to <body> so ancestor backdrop-filter/transform can't trap the
  // fixed-position layer — otherwise the bar renders inside the backdrop-
  // blurred form and floats far below the viewport.
  const node = (
    <div
      data-testid="sticky-price-bar"
      className="fixed bottom-0 inset-x-0 z-40 pointer-events-none px-3 pb-3 sm:pb-4 pr-20 sm:pr-24 md:pr-3"
    >
      <div className="mx-auto max-w-4xl pointer-events-auto animate-in slide-in-from-bottom-4 fade-in-0 duration-500 motion-reduce:animate-none">
        <div className="flex items-center gap-3 sm:gap-4 rounded-2xl border border-[#C5A059]/40 bg-black/85 backdrop-blur-xl px-4 sm:px-5 py-3 shadow-[0_12px_40px_rgba(0,0,0,0.6)]">
          <div className="min-w-0 flex-1">
            <p className="text-[9px] uppercase tracking-[0.25em] text-[#C5A059] truncate">
              {t("form.sticky_route_prefix")} · {vehicleClass.labelI18n ? t(vehicleClass.labelI18n) : vehicleClass.label}
            </p>
            <p
              data-testid="sticky-route-summary"
              className="text-xs sm:text-sm text-zinc-300 truncate"
              title={routeLabel}
            >
              {routeLabel}
            </p>
            {metricsLine && (
              <p
                data-testid="sticky-route-metrics"
                className="mt-0.5 text-[10px] text-zinc-500 truncate tabular-nums"
              >
                {metricsLine}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end shrink-0">
            <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">
              {t("form.sticky_total_label")}
            </span>
            {showRecalc && price === null ? (
              <span className="inline-flex items-center gap-1 text-sm text-zinc-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {t("form.sticky_recalculating")}
              </span>
            ) : (
              <span
                data-testid="sticky-price-value"
                className={`font-mono text-xl sm:text-2xl font-semibold tabular-nums transition-opacity ${
                  showRecalc ? "text-[#C5A059]/60" : "text-[#C5A059]"
                }`}
              >
                €{Number(price).toFixed(2)}
              </span>
            )}
          </div>
          <button
            type="button"
            data-testid="sticky-book-btn"
            onClick={scrollToSubmit}
            disabled={submitting}
            className="shrink-0 rounded-xl bg-[#C5A059] hover:bg-[#B48F4A] text-black text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2 sm:py-2.5 transition-colors disabled:opacity-60"
          >
            {t("form.sticky_book_now")} →
          </button>
        </div>
      </div>
    </div>
  );
  return typeof document !== "undefined"
    ? createPortal(node, document.body)
    : node;
}
